import {
  Component,
  ElementRef,
  AfterViewInit,
  OnChanges,
  Input,
} from '@angular/core';
import { ChartComponent } from '../chart.component';
import { select, Selection } from 'd3-selection';
import { axisBottom, axisLeft } from 'd3-axis';
import { line } from 'd3-shape';
import { scaleLinear, scaleTime } from 'd3-scale';

import * as moment from 'moment';

@Component({
  selector: 'app-line-chart-base',
  template: '',
})
export class LineChartBaseComponent
  extends ChartComponent
  implements AfterViewInit, OnChanges {
  @Input() data: any[];
  @Input() xValue = 'x';
  @Input() yValue = 'y';
  @Input() color = 'rgb(255, 102, 0)';
  @Input() useGrid: boolean;
  @Input() xTickValues: number[];
  @Input() yTickValues: number[];
  @Input() scaleAxisXByTime: boolean = false;
  private xScale: any;
  private yScale: any;
  private xScaleMax: any = 0;
  private xScaleMin: any = 0;
  private yScaleMax: number;

  constructor(protected el: ElementRef) {
    super(el);
  }

  ngAfterViewInit() {
    this.drawChart();
  }

  ngOnChanges(): void {
    if (!this.data) {
      return;
    }

    this.drawChart();
  }

  drawChart() {
    this.updateChartDimentions();
    if (this.width && this.height && this.data) {
      this.updateDimensions();
      this.initElements();
      this.setScales();
      this.drawLine();
      this.addAxis();
    }
  }

  private updateDimensions() {
    this.height = this.height - this.margin.top - this.margin.bottom;
    this.width = this.width - this.margin.left - this.margin.right;
  }

  private initElements() {
    this.svg = select(this.chartElement)
      .append('svg')
      .attr('width', this.width + this.margin.left + this.margin.right)
      .attr('height', this.height + this.margin.top + this.margin.bottom)
      .append('g')
      .attr('transform', `translate(${this.margin.left},${this.margin.top})`);
  }

  private setScales() {
    if (this.scaleAxisXByTime) {
      this.data.map((d) => {
        d.date = moment(d.date, 'DD-MM-YYYY').toDate();
      });
      this.data = this.data.sort((a, b) => {
        return a.date.getTime() - b.date.getTime();
      });
      this.xScaleMin = this.data[0][this.xValue];
      this.xScaleMax = this.data[this.data.length - 1][this.xValue];
      this.xScale = scaleTime()
        .domain([this.xScaleMin, this.xScaleMax])
        .range([0, this.width]);
    } else {
      this.xScaleMin = this.data[0][this.xValue];
      this.xScaleMax = this.data[this.data.length - 1][this.xValue];
      this.xScale = scaleLinear()
        .domain([0, this.data.length - 1])
        .range([0, this.width]);
    }
    const _yValuesList = this.data.map((yScaleItem) =>
      Number(yScaleItem[this.yValue])
    );
    this.yScaleMax = Math.max(..._yValuesList);
    this.yScale = scaleLinear()
      .domain([0, this.yScaleMax])
      .range([this.height, 0]);
  }

  private drawLine() {
    const valueline = line()
      .x((d, i) => this.xScale(this.scaleAxisXByTime ? d[this.xValue] : i)) // set the x values for the line generator
      .y((d: any) => this.yScale(d[this.yValue])); // set the y values for the line generator
    this.svg
      .append('path')
      .data([this.data])
      .attr('class', 'line')
      .attr('fill', 'none')
      .attr('stroke', this.color)
      .attr('stroke-width', 1.9)
      .attr('d', valueline);
  }

  private addAxis() {
    // Add the X Axis
    this.svg
      .append('g')
      .attr('transform', `translate(0,${this.height})`)
      .call(this.prepareXAxis.bind(this));

    // Add the Y Axis
    this.svg.append('g').call(this.prepareYAxis.bind(this));
  }

  private prepareXAxis(axis: Selection<SVGGElement, {}, any, {}>) {
    if (this.useGrid) {
      const _tickValuesList = this.scaleAxisXByTime
        ? [this.xScaleMin, this.xScaleMax]
        : this.xTickValues;
      this.svg
        .append('g')
        .attr('class', 'grid')
        .attr('transform', 'translate(0,' + this.height + ')')
        .call(
          axisBottom(this.xScale)
            .tickValues(_tickValuesList)
            .tickPadding(15)
            .tickSizeInner(-this.height)
            .tickSizeOuter(10)
        )
        .selectAll('.tick > line')
        .style('stroke', 'lightgrey')
        .style('stroke-opacity', 0.7);
    } else {
      axis.call(axisBottom(this.xScale));
    }
  }

  private prepareYAxis(axis: Selection<SVGGElement, {}, any, {}>) {
    if (this.useGrid) {
      const _tickValuesList = this.yTickValues || [0, this.yScaleMax];
      this.svg
        .append('g')
        .attr('class', 'grid')
        .call(
          axisLeft(this.yScale)
            .tickValues(_tickValuesList)
            .tickPadding(15)
            .tickSizeInner(-this.width)
            .tickSizeOuter(10)
        )
        .selectAll('.tick > line')
        .style('stroke', 'lightgrey')
        .style('stroke-opacity', 0.7);
    } else {
      axis.call(axisLeft(this.yScale));
    }
  }
}
